import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
} from '@merritrac/shared/components';
import { Download, FileText, Trash2, Upload, UploadCloud } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { usePortalAuth } from '../hooks/usePortalAuth.js';

import type { ChangeEvent, DragEvent, ReactElement } from 'react';

/* ── Types ── */

type DocStatus = 'uploaded' | 'processing' | 'approved';

interface Document {
  id: string;
  name: string;
  type: string;
  uploadDate: string;
  size: string;
  status: DocStatus;
  url?: string;
}

const initialDocuments: Document[] = [];

/* ── Status config ── */

type GlassBadgeVariant = 'info' | 'success' | 'warning' | 'danger' | 'default';

const statusBadgeVariant: Record<DocStatus, GlassBadgeVariant> = {
  uploaded: 'info',
  processing: 'warning',
  approved: 'success',
};

const statusLabel: Record<DocStatus, string> = {
  uploaded: 'Uploaded',
  processing: 'Processing',
  approved: 'Approved',
};

/* ── Helpers ── */

function getFileType(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const docExts = ['doc', 'docx'];
  if (ext === 'pdf') {
    return 'PDF';
  }
  if (imageExts.includes(ext)) {
    return 'Image';
  }
  if (docExts.includes(ext)) {
    return 'Word';
  }
  if (ext === 'xlsx' || ext === 'xls') {
    return 'Excel';
  }
  return ext.toUpperCase();
}

function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${String(Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(isoString: string): string {
  return isoString.split('T')[0] ?? isoString;
}

/* ── API helpers ── */

async function uploadDocument(file: File, token: string): Promise<Document> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        // eslint-disable-next-line no-console
        console.info(`Upload progress: ${String(percent)}%`);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText) as {
            id: string;
            url: string;
            filename: string;
            size: number;
            uploadedAt: string;
          };
          resolve({
            id: response.id,
            name: response.filename,
            type: getFileType(response.filename),
            uploadDate: formatDate(response.uploadedAt),
            size: formatSize(response.size),
            status: 'uploaded',
            url: response.url,
          });
        } catch {
          reject(new Error('Failed to parse upload response'));
        }
      } else {
        reject(new Error(`Upload failed: ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed: network error'));
    });

    xhr.open('POST', '/documents');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
}

async function deleteDocument(id: string, token: string): Promise<void> {
  const res = await fetch(`/documents/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`Delete failed: ${res.statusText}`);
  }
}

/* ── Upload area ── */

interface UploadAreaProps {
  onFilesSelected: (files: FileList) => void;
  isDragging: boolean;
  onDragEnter: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: DragEvent<HTMLDivElement>) => void;
  onDrop: (e: DragEvent<HTMLDivElement>) => void;
}

function UploadArea({
  onFilesSelected,
  isDragging,
  onDragEnter,
  onDragLeave,
  onDrop,
}: UploadAreaProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleClick(): void {
    inputRef.current?.click();
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    const { files } = e.target;
    if (files !== null && files.length > 0) {
      onFilesSelected(files);
    }
    // Reset input so the same file can be re-uploaded
    if (inputRef.current !== null) {
      inputRef.current.value = '';
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`flex flex-col items-center justify-center gap-3 p-10 rounded-lg border-2 border-dashed cursor-pointer transition-colors select-none ${
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-[var(--glass-border)] hover:border-primary/50 hover:bg-[var(--glass-bg-hover)]'
      }`}
      aria-label="Upload files — click or drag and drop"
    >
      <UploadCloud
        size={36}
        className={isDragging ? 'text-primary' : 'text-text-tertiary'}
        aria-hidden="true"
      />
      <div className="text-center">
        <p className="text-sm font-medium text-text">
          {isDragging ? 'Drop files here' : 'Click to upload or drag and drop'}
        </p>
        <p className="text-xs text-text-tertiary mt-1">
          PDF, images (JPG, PNG), or Word/Excel documents
        </p>
        <p className="text-xs text-text-tertiary">Maximum file size: 25 MB per file</p>
      </div>
      <GlassButton variant="secondary" size="sm" tabIndex={-1} aria-hidden="true">
        <Upload size={13} />
        Choose Files
      </GlassButton>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx"
        className="sr-only"
        onChange={handleChange}
        aria-hidden="true"
        tabIndex={-1}
      />
    </div>
  );
}

/* ── Page ── */

async function fetchDocuments(token: string): Promise<Document[]> {
  const res = await fetch('/documents', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`Failed to load documents: ${res.statusText}`);
  }
  const body = (await res.json()) as {
    documents: {
      id: string;
      url: string;
      filename: string;
      size: number | null;
      contentType: string | null;
      uploadedAt: string;
    }[];
  };
  return body.documents.map((d) => ({
    id: d.id,
    name: d.filename,
    type: getFileType(d.filename),
    uploadDate: formatDate(d.uploadedAt),
    size: formatSize(d.size ?? 0),
    status: 'uploaded' as DocStatus,
    url: d.url,
  }));
}

export function DocumentsPage(): ReactElement {
  const { token } = usePortalAuth();
  const [documents, setDocuments] = useState<Document[]>(initialDocuments);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token === null) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchDocuments(token)
      .then((docs) => {
        if (!cancelled) {
          setDocuments(docs);
        }
      })
      .catch((err: unknown) => {
        console.error('Failed to load documents:', err);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return (): void => {
      cancelled = true;
    };
  }, [token]);

  async function addFiles(files: FileList): Promise<void> {
    if (token === null) {
      return;
    }
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      try {
        const doc = await uploadDocument(file, token);
        setDocuments((prev) => [doc, ...prev]);
      } catch (err) {
        console.error('Upload failed:', file.name, err);
      }
    }
  }

  function handleFilesSelected(files: FileList): void {
    void addFiles(files);
  }

  function handleDragEnter(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDragging(false);
    const { files } = e.dataTransfer;
    if (files.length > 0) {
      void addFiles(files);
    }
  }

  function handleDownload(doc: Document): void {
    // eslint-disable-next-line eqeqeq
    if (doc.url != null && doc.url !== '') {
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (token === null) {
      return;
    }
    try {
      await deleteDocument(id, token);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error('Delete failed:', id, err);
    }
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-semibold text-text">Documents</h1>
        <p className="text-sm text-text-secondary mt-0.5">
          {String(documents.length)} document{documents.length !== 1 ? 's' : ''} — service reports
          and account documentation
        </p>
      </div>

      {/* Upload area */}
      <GlassCard>
        <GlassCardHeader>
          <h2 className="text-base font-semibold text-text">Upload Documents</h2>
        </GlassCardHeader>
        <GlassCardContent>
          <UploadArea
            onFilesSelected={handleFilesSelected}
            isDragging={isDragging}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          />
        </GlassCardContent>
      </GlassCard>

      {/* Document list */}
      <GlassCard>
        <GlassCardHeader>
          <h2 className="text-base font-semibold text-text">
            Your Documents
            <span className="ml-2 text-sm font-normal text-text-secondary">
              ({String(documents.length)} files)
            </span>
          </h2>
        </GlassCardHeader>

        {loading ? (
          <GlassCardContent>
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <p className="text-sm text-text-secondary">Loading documents...</p>
            </div>
          </GlassCardContent>
        ) : documents.length === 0 ? (
          <GlassCardContent>
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <FileText size={40} className="text-text-tertiary" />
              <div className="text-center">
                <p className="text-sm font-medium text-text">No documents yet</p>
                <p className="text-xs text-text-secondary mt-1">
                  Upload documents using the area above.
                </p>
              </div>
            </div>
          </GlassCardContent>
        ) : (
          <GlassTable>
            <GlassTableHead>
              <GlassTableRow>
                <GlassTableHeaderCell>File Name</GlassTableHeaderCell>
                <GlassTableHeaderCell className="whitespace-nowrap hidden sm:table-cell">
                  Type
                </GlassTableHeaderCell>
                <GlassTableHeaderCell className="whitespace-nowrap hidden md:table-cell">
                  Uploaded
                </GlassTableHeaderCell>
                <GlassTableHeaderCell className="whitespace-nowrap hidden lg:table-cell">
                  Size
                </GlassTableHeaderCell>
                <GlassTableHeaderCell className="whitespace-nowrap">Status</GlassTableHeaderCell>
                <GlassTableHeaderCell className="text-right whitespace-nowrap">
                  Actions
                </GlassTableHeaderCell>
              </GlassTableRow>
            </GlassTableHead>
            <GlassTableBody>
              {documents.map((doc) => (
                <GlassTableRow key={doc.id}>
                  <GlassTableCell>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={16} className="text-text-tertiary shrink-0" />
                      <span className="text-text font-medium truncate max-w-[200px] sm:max-w-xs">
                        {doc.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 md:hidden">
                      <span className="text-xs text-text-tertiary">{doc.uploadDate}</span>
                      <span className="text-xs text-text-tertiary">{doc.size}</span>
                    </div>
                  </GlassTableCell>
                  <GlassTableCell className="text-text-secondary hidden sm:table-cell">
                    {doc.type}
                  </GlassTableCell>
                  <GlassTableCell className="text-text-secondary whitespace-nowrap hidden md:table-cell">
                    {doc.uploadDate}
                  </GlassTableCell>
                  <GlassTableCell className="text-text-secondary whitespace-nowrap hidden lg:table-cell">
                    {doc.size}
                  </GlassTableCell>
                  <GlassTableCell>
                    <GlassBadge variant={statusBadgeVariant[doc.status]}>
                      {statusLabel[doc.status]}
                    </GlassBadge>
                  </GlassTableCell>
                  <GlassTableCell>
                    <div className="flex items-center justify-end gap-1">
                      <GlassButton
                        variant="ghost"
                        size="sm"
                        aria-label={`Download ${doc.name}`}
                        onClick={() => {
                          handleDownload(doc);
                        }}
                      >
                        <Download size={13} />
                        <span className="hidden sm:inline">Download</span>
                      </GlassButton>
                      <GlassButton
                        variant="ghost"
                        size="sm"
                        aria-label={`Delete ${doc.name}`}
                        className="text-danger hover:text-danger hover:bg-danger-soft"
                        onClick={() => {
                          void handleDelete(doc.id);
                        }}
                      >
                        <Trash2 size={13} />
                        <span className="hidden sm:inline">Delete</span>
                      </GlassButton>
                    </div>
                  </GlassTableCell>
                </GlassTableRow>
              ))}
            </GlassTableBody>
          </GlassTable>
        )}
      </GlassCard>
    </div>
  );
}
